'use client'

import { useIsDesktop } from '../../hooks'
import { Row } from '../row'
import { cn } from '../../lib/utils'
import { useState, useRef, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import type { BannerCarouselProps } from './banner-carousel.types'
import 'swiper/css'
import { Skeleton } from '..'

export const BannerCarousel = ({
  items,
  className,
  LinkComponent,
  ImageComponent,
}: BannerCarouselProps) => {
  const Link = LinkComponent || 'a'
  const Image = ImageComponent || 'img'
  const isDesktop = useIsDesktop()
  const swiperRef = useRef<SwiperType | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (!isDesktop && swiperRef.current && items.length > 0) {
      swiperRef.current?.slideNext()
    }
  }, [isDesktop, items.length])

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  if (!items.length) return null

  // Desktop: Show items in grid layout, max 3 per row
  if (isDesktop) {
    return (
      <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-md', className)}>
        {items.map((item, index) => (
          <Link
            key={index}
            href={item.linkUrl}
            className='relative w-full aspect-[16/6] sm:h-full rounded-md overflow-hidden block'
          >
            <Image
              src={item.imageUrl}
              alt={`Banner ${index + 1}`}
              className='w-full h-full object-cover'
            />
          </Link>
        ))}
      </div>
    )
  }

  // Mobile: Swiper carousel with infinite scroll
  return (
    <div className={cn(className, 'relative')}>
      {!isLoaded && (
        <>
          <Skeleton className='absolute top-0 left-[11%] w-[78%] h-[123px] mx-auto rounded-md sm:hidden' />
          <Row className={cn('h-[146px] gap-md hidden sm:flex')}>
            <Skeleton className='w-full h-full mx-auto rounded-md' />
            <Skeleton className='w-full h-full mx-auto rounded-md' />
            <Skeleton className='w-full h-full mx-auto rounded-md' />
          </Row>
        </>
      )}
      <Swiper
        spaceBetween={16}
        slidesPerView={1.3}
        centeredSlides={true}
        // loop={true}
        dir='rtl'
        direction='horizontal'
        onSwiper={(swiper) => {
          swiperRef.current = swiper
        }}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        onSlideChange={(swiper) => {
          setCurrentIndex(swiper.realIndex)
        }}
        className={cn(!isLoaded && 'opacity-0 !max-h-[122px]')}
      >
        {items.map((item, index) => (
          <SwiperSlide key={index}>
            <Link
              href={item.linkUrl}
              className='block relative w-full aspect-[16/6] sm:h-full rounded-md overflow-hidden sm:hidden'
            >
              <Image
                src={item.imageUrl}
                alt={`Banner ${index + 1}`}
                className='w-full h-full object-cover'
              />
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Indicators */}
      <Row className='justify-center items-center gap-2xs mt-2xl sm:hidden'>
        {items.map((_, index) => (
          <div
            key={index}
            className={cn(
              'rounded-full transition-all',
              index === currentIndex
                ? 'w-xl h-[2px] bg-base-main'
                : 'w-xl h-[2px] bg-neutral-surface-20',
            )}
          />
        ))}
      </Row>
    </div>
  )
}
